import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import {
  Layers, Plus, Play, Pencil, Trash2, Loader2, ArrowUp, ArrowDown, CheckCircle2,
  AlertTriangle, Info, CalendarClock, X, ListOrdered,
} from 'lucide-react';

const DIAS_SEMANA = [
  { v: 0, l: 'Lunes' }, { v: 1, l: 'Martes' }, { v: 2, l: 'Miércoles' },
  { v: 3, l: 'Jueves' }, { v: 4, l: 'Viernes' }, { v: 5, l: 'Sábado' }, { v: 6, l: 'Domingo' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const grupoFreqLabel = (g) => {
  if (!g.activa || g.frecuencia === 'manual') return 'Sólo manual';
  if (g.frecuencia === 'mensual') return `Mensual · ${g.dia_mes === 'ultimo' ? 'último día' : 'primer día'} · ${g.hora}`;
  if (g.frecuencia === 'semanal') return `Semanal · ${DIAS_SEMANA.find((d) => d.v === g.dia_semana)?.l || ''} · ${g.hora}`;
  if (g.frecuencia === 'diaria') return `Diaria · ${g.hora}`;
  return g.frecuencia;
};

const GrupoBadge = ({ g }) => {
  const s = g.last_status;
  if (s === 'en_ejecucion') return <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100"><Loader2 size={12} className="mr-1 animate-spin" />Ejecutando…</Badge>;
  if (s === 'ok') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 size={12} className="mr-1" />Última: OK</Badge>;
  if (s === 'parcial') { const n = g.last_result?.error || 0; return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle size={12} className="mr-1" />Parcial · {n} con error</Badge>; }
  if (s === 'error') return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle size={12} className="mr-1" />Última: Error</Badge>;
  return <Badge variant="secondary">Sin ejecutar</Badge>;
};

const MemberResultRow = ({ d }) => {
  const map = {
    ok: { icon: CheckCircle2, cls: 'text-emerald-600' },
    error: { icon: AlertTriangle, cls: 'text-rose-600' },
    sin_datos: { icon: Info, cls: 'text-slate-400' },
  };
  const m = map[d.status] || map.sin_datos;
  const Icon = m.icon;
  return (
    <div className="flex items-start gap-2 text-xs py-1" data-testid={`grupo-det-${d.aid}`}>
      <Icon size={13} className={`${m.cls} mt-0.5 shrink-0`} />
      <span className="font-medium text-slate-700 shrink-0">{d.nombre}</span>
      <span className="text-slate-400 truncate">· {d.error || d.resumen || d.status}</span>
    </div>
  );
};

const GruposSecuenciales = ({ autos = [], onRefresh, presetMembers, onPresetConsumed }) => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/kpis/automatizaciones/grupos');
      setGrupos(r.data.grupos || []);
      return r.data.grupos || [];
    } catch (e) {
      if (!silent) toast.error('Error al cargar los grupos');
    } finally {
      if (!silent) setLoading(false);
    }
    return [];
  }, []);
  useEffect(() => { load(); }, [load]);

  // Polling mientras algún grupo esté ejecutándose
  useEffect(() => {
    const anyRunning = grupos.some((g) => g.last_status === 'en_ejecucion');
    if (!anyRunning) return;
    const t = setTimeout(async () => {
      await load(true);
      if (onRefresh) onRefresh();
    }, 5000);
    return () => clearTimeout(t);
  }, [grupos, load, onRefresh]);

  const openCreate = useCallback((preset) => {
    setEditing(null);
    setForm({ nombre: '', miembros: preset || [], frecuencia: 'manual', dia_mes: 'primero', dia_semana: 1, hora: '02:00', activa: false });
    setDialogOpen(true);
  }, []);

  const openEdit = (g) => {
    setEditing(g);
    setForm({ nombre: g.nombre, miembros: [...(g.miembros || [])], frecuencia: g.frecuencia || 'manual', dia_mes: g.dia_mes || 'primero', dia_semana: g.dia_semana ?? 1, hora: g.hora || '02:00', activa: !!g.activa });
    setDialogOpen(true);
  };

  // Preset desde la selección ad-hoc ("Guardar como grupo")
  useEffect(() => {
    if (presetMembers && presetMembers.length) {
      openCreate(presetMembers);
      if (onPresetConsumed) onPresetConsumed();
    }
  }, [presetMembers, openCreate, onPresetConsumed]);

  const nombreDe = (aid) => autos.find((a) => a.id === aid)?.nombre || aid;

  const toggleMember = (aid) => setForm((f) => {
    const has = (f.miembros || []).includes(aid);
    return { ...f, miembros: has ? f.miembros.filter((x) => x !== aid) : [...(f.miembros || []), aid] };
  });
  const move = (idx, dir) => setForm((f) => {
    const arr = [...(f.miembros || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...f, miembros: arr };
  });

  const save = async () => {
    const nombre = (form.nombre || '').trim();
    if (!nombre) { toast.error('Poné un nombre al grupo'); return; }
    if (!(form.miembros || []).length) { toast.error('Elegí al menos una automatización'); return; }
    setSaving(true);
    try {
      const payload = { nombre, miembros: form.miembros, frecuencia: form.frecuencia, dia_mes: form.dia_mes, dia_semana: form.dia_semana, hora: form.hora, activa: form.activa };
      if (editing) await api.put(`/kpis/automatizaciones/grupos/${editing.id}`, payload);
      else await api.post('/kpis/automatizaciones/grupos', payload);
      toast.success(editing ? 'Grupo actualizado' : 'Grupo creado');
      setDialogOpen(false); setEditing(null);
      await load(true);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el grupo');
    } finally {
      setSaving(false);
    }
  };

  const run = async (g) => {
    try {
      await api.post(`/kpis/automatizaciones/grupos/${g.id}/ejecutar`);
      toast.success('Ejecutando el grupo en secuencia. El avance se actualiza solo…');
      setGrupos((prev) => prev.map((x) => (x.id === g.id
        ? { ...x, last_status: 'en_ejecucion', progreso: { idx: 0, total: (x.miembros || []).length, actual_aid: (x.miembros || [])[0] } }
        : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar el grupo');
    }
  };

  const del = async () => {
    try {
      await api.delete(`/kpis/automatizaciones/grupos/${deleteId}`);
      toast.success('Grupo eliminado');
      setDeleteId(null);
      await load(true);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="mb-8" data-testid="grupos-secuenciales">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers size={18} className="text-indigo-600" />
          <h2 className="text-lg font-semibold text-slate-900">Grupos de ejecución secuencial</h2>
        </div>
        <Button size="sm" onClick={() => openCreate([])} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="grupo-nuevo-btn">
          <Plus size={15} className="mr-1.5" /> Nuevo grupo
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Agrupá varias automatizaciones para que corran una tras otra (cuando termina una, arranca la siguiente). Ideal para evitar que los procesos pesados se pisen.</p>

      {loading ? (
        <div className="flex items-center py-8 text-slate-400"><Loader2 className="animate-spin mr-2" size={16} /> Cargando grupos…</div>
      ) : grupos.length === 0 ? (
        <Card className="p-6 text-center border-dashed border-2 border-slate-200 bg-slate-50/50" data-testid="grupos-empty">
          <ListOrdered size={26} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">Todavía no creaste grupos. Creá uno con “Nuevo grupo”, o seleccioná varias automatizaciones abajo y guardalas como grupo.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {grupos.map((g) => {
            const running = g.last_status === 'en_ejecucion';
            const prog = g.progreso;
            const lr = g.last_result;
            const isOpen = !!expanded[g.id];
            return (
              <Card key={g.id} className="p-4 lg:p-5" data-testid={`grupo-card-${g.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900" data-testid={`grupo-nombre-${g.id}`}>{g.nombre}</h3>
                      <GrupoBadge g={g} />
                      <span className="text-xs text-slate-500 flex items-center gap-1"><CalendarClock size={12} /> {grupoFreqLabel(g)}</span>
                      {g.activa && g.frecuencia !== 'manual' && g.next_run && (
                        <span className="text-xs text-slate-400">· Próxima: {fmtDate(g.next_run)}</span>
                      )}
                    </div>

                    {/* miembros en orden */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {(g.miembros_info || []).map((m, i) => (
                        <span key={m.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 rounded-full pl-1.5 pr-2 py-0.5" data-testid={`grupo-miembro-${g.id}-${m.id}`}>
                          <span className="w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                          {m.nombre}
                        </span>
                      ))}
                      {(g.miembros_info || []).length === 0 && <span className="text-xs text-slate-400">Sin automatizaciones</span>}
                    </div>

                    {/* progreso en vivo */}
                    {running && prog && (
                      <div className="mt-3" data-testid={`grupo-progreso-${g.id}`}>
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                          <span>Ejecutando {Math.min((prog.idx || 0) + 1, prog.total)} de {prog.total}: <span className="font-medium text-slate-700">{nombreDe(prog.actual_aid)}</span></span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-sky-500 transition-all duration-500" style={{ width: `${prog.total ? ((prog.idx || 0) / prog.total) * 100 : 0}%` }} />
                        </div>
                      </div>
                    )}

                    {/* resultado última corrida */}
                    {!running && lr && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500">
                          Última corrida: {fmtDate(g.last_run)} · <span className="text-emerald-600 font-medium">{lr.ok} ok</span>
                          {lr.skip ? <span className="text-slate-400"> · {lr.skip} sin datos</span> : null}
                          {lr.error ? <span className="text-rose-600 font-medium"> · {lr.error} con error</span> : null}
                        </p>
                        {(lr.detalles || []).length > 0 && (
                          <button type="button" onClick={() => setExpanded((e) => ({ ...e, [g.id]: !isOpen }))} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 mt-1" data-testid={`grupo-detalle-toggle-${g.id}`}>
                            {isOpen ? 'Ocultar detalle' : 'Ver detalle por automatización'}
                          </button>
                        )}
                        {isOpen && (
                          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2" data-testid={`grupo-detalle-${g.id}`}>
                            {(lr.detalles || []).map((d) => <MemberResultRow key={d.aid} d={d} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-row lg:flex-col gap-2 shrink-0">
                    <Button size="sm" onClick={() => run(g)} disabled={running} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`grupo-ejecutar-${g.id}`}>
                      {running ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Play size={15} className="mr-1.5" />} Ejecutar ahora
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(g)} disabled={running} data-testid={`grupo-editar-${g.id}`}>
                      <Pencil size={14} className="mr-1.5" /> Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(g.id)} disabled={running} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`grupo-eliminar-${g.id}`}>
                      <Trash2 size={14} className="mr-1.5" /> Eliminar
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="grupo-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar grupo' : 'Nuevo grupo secuencial'}</DialogTitle>
            <DialogDescription>Elegí las automatizaciones, definí el orden y (opcional) una programación automática.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-5">
              <div>
                <Label className="text-sm">Nombre del grupo</Label>
                <Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Cierre mensual, Scrapers Waya…" className="mt-1" data-testid="grupo-nombre-input" />
              </div>

              <div>
                <Label className="text-sm">Automatizaciones</Label>
                <div className="mt-1.5 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-auto">
                  {autos.map((a) => {
                    const checked = (form.miembros || []).includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`grupo-pick-${a.id}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggleMember(a.id)} data-testid={`grupo-pick-check-${a.id}`} />
                        <div className="min-w-0">
                          <p className="text-sm text-slate-800 truncate">{a.nombre}</p>
                          <p className="text-xs text-slate-400">{a.seccion || a.fuente}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {(form.miembros || []).length > 0 && (
                <div>
                  <Label className="text-sm flex items-center gap-1.5"><ListOrdered size={14} /> Orden de ejecución</Label>
                  <div className="mt-1.5 rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="grupo-orden-list">
                    {(form.miembros || []).map((aid, i) => (
                      <div key={aid} className="flex items-center gap-2 px-3 py-2" data-testid={`grupo-orden-${aid}`}>
                        <span className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        <span className="text-sm text-slate-700 flex-1 truncate">{nombreDe(aid)}</span>
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`grupo-up-${aid}`}><ArrowUp size={14} /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === (form.miembros.length - 1)} onClick={() => move(i, 1)} data-testid={`grupo-down-${aid}`}><ArrowDown size={14} /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => toggleMember(aid)} data-testid={`grupo-remove-${aid}`}><X size={14} /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-sm font-medium">Programación automática</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Si la activás, el grupo corre solo en la agenda elegida. Las automatizaciones del grupo dejan de correr por su cuenta para no duplicar.</p>
                  </div>
                  <Switch checked={!!form.activa} onCheckedChange={(v) => setForm((f) => ({ ...f, activa: v, frecuencia: v && f.frecuencia === 'manual' ? 'mensual' : f.frecuencia }))} data-testid="grupo-activa-switch" />
                </div>
                {form.activa && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600">Frecuencia</Label>
                      <Select value={form.frecuencia === 'manual' ? 'mensual' : form.frecuencia} onValueChange={(v) => setForm((f) => ({ ...f, frecuencia: v }))}>
                        <SelectTrigger className="mt-1 h-9" data-testid="grupo-frecuencia"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="diaria">Diaria</SelectItem>
                          <SelectItem value="semanal">Semanal</SelectItem>
                          <SelectItem value="mensual">Mensual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {form.frecuencia === 'mensual' && (
                      <div>
                        <Label className="text-xs text-slate-600">Día del mes</Label>
                        <Select value={form.dia_mes} onValueChange={(v) => setForm((f) => ({ ...f, dia_mes: v }))}>
                          <SelectTrigger className="mt-1 h-9" data-testid="grupo-diames"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="primero">Primer día del mes</SelectItem>
                            <SelectItem value="ultimo">Último día del mes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {form.frecuencia === 'semanal' && (
                      <div>
                        <Label className="text-xs text-slate-600">Día de la semana</Label>
                        <Select value={String(form.dia_semana)} onValueChange={(v) => setForm((f) => ({ ...f, dia_semana: parseInt(v, 10) }))}>
                          <SelectTrigger className="mt-1 h-9" data-testid="grupo-diasemana"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DIAS_SEMANA.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-slate-600">Hora</Label>
                      <Input type="time" value={form.hora} onChange={(e) => setForm((f) => ({ ...f, hora: e.target.value }))} className="mt-1 h-9" data-testid="grupo-hora" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }} data-testid="grupo-cancelar">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="grupo-guardar">
              {saving ? <Loader2 size={15} className="animate-spin mr-1.5" /> : null} {editing ? 'Guardar cambios' : 'Crear grupo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="grupo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este grupo?</AlertDialogTitle>
            <AlertDialogDescription>Se borra el grupo (no las automatizaciones). Si tenía programación, sus automatizaciones vuelven a correr con su horario individual.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="grupo-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700" data-testid="grupo-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GruposSecuenciales;
